import React, { useEffect, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Loader2, Sparkles, Library } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { hasOwnerAccess } from '../../lib/owner'
import { supabase } from '../../lib/supabaseClient'

// ReadingRecapPage — /reading/recap/:yearQuarter
//
// Per-viewer quarterly recap. Each pair member has their own
// recap row written by reading-quarterly-recap on the 1st of
// Jan/Apr/Jul/Oct. The page reads the row matching the viewer
// (RLS exposes both pair members' rows but we filter to self —
// the partner's recap is theirs to share if they want).
export function ReadingRecapPage() {
  const { t, i18n } = useTranslation()
  const locale = i18n.language === 'ar' ? 'ar' : 'en'
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const { yearQuarter } = useParams()
  const [recap, setRecap] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user || !yearQuarter) return undefined
    let alive = true
    ;(async () => {
      setLoading(true)
      const { data } = await supabase
        .from('reading_recaps')
        .select('id, user_id, year_quarter, book_count, narrative_en, narrative_ar, generated_at')
        .eq('user_id', user.id)
        .eq('year_quarter', yearQuarter)
        .maybeSingle()
      if (alive) {
        setRecap(data || null)
        setLoading(false)
      }
    })()
    return () => { alive = false }
  }, [user, yearQuarter])

  if (!authLoading && user && !hasOwnerAccess(user)) {
    return <Navigate to="/" replace />
  }
  if (authLoading || loading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin opacity-60" />
      </div>
    )
  }

  // Pretty label — "2026 · Q2" works in both locales without
  // pulling a quarter-name translation table.
  let pretty = yearQuarter
  try {
    const [y, q] = (yearQuarter || '').split('-')
    if (y && q) pretty = `${y} · ${q}`
  } catch { /* keep raw */ }

  const narrative = recap
    ? (locale === 'ar' ? (recap.narrative_ar || recap.narrative_en) : (recap.narrative_en || recap.narrative_ar))
    : null
  const paragraphs = (narrative || '').split(/\n{2,}/).map((p) => p.trim()).filter(Boolean)

  return (
    <div className="mx-auto max-w-md space-y-5 p-4">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => navigate('/reading')}
          className="grid h-9 w-9 place-items-center rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800"
          aria-label={t('reading.back')}
        >
          <ArrowLeft className="h-5 w-5 rtl:-scale-x-100" />
        </button>
        <div className="flex items-center gap-1.5">
          <Sparkles className="h-4 w-4 text-rose-500" />
          <h1 dir="auto" className="text-lg font-semibold">{pretty}</h1>
        </div>
      </div>

      {!recap ? (
        <p dir="auto" className="pt-8 text-center text-sm opacity-70">{t('reading.recapNotFound')}</p>
      ) : recap.book_count === 0 ? (
        <div className="space-y-3 pt-6 text-center">
          <Library className="mx-auto h-8 w-8 opacity-40" />
          <p dir="auto" className="text-sm opacity-70">{t('reading.recapEmpty')}</p>
        </div>
      ) : (
        <>
          <div className="rounded-2xl bg-neutral-100 px-4 py-3 text-center dark:bg-neutral-800">
            <div className="text-3xl font-bold leading-none">{recap.book_count}</div>
            <div dir="auto" className="mt-1 text-[11px] uppercase tracking-wide opacity-60">
              {t('reading.recapBookCount')}
            </div>
          </div>
          {paragraphs.length > 0 ? (
            <div className="space-y-3 rounded-2xl bg-rose-500/5 px-4 py-4">
              {paragraphs.map((p, i) => (
                <p key={i} dir="auto" className="text-sm leading-relaxed">{p}</p>
              ))}
            </div>
          ) : (
            <p dir="auto" className="text-center text-sm opacity-70">{t('reading.recapPending')}</p>
          )}
        </>
      )}
    </div>
  )
}
